import React, { useContext, useMemo } from 'react';
import Select from 'react-select';

import Notification from '../Notification';
import Loader from '../Loader';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import useOptionsLookup from './hooks/useOptionsLookup';
import { Trans } from '@lingui/react/macro';
import { AuthContext } from '../../context/AuthContext';
import useTestId from '../../hooks/useTestId';

function LookupMultiSelectDropdownField(props) {
  const {
    formData,
    registry,
    schema: { title, lookup, items: { type: itemsType } = {} },
    fieldPathId: { $id, path: fieldPath } = {},
    uiSchema: { 'ui:readonly': uiReadonly } = {},
    required,
    disabled,
    readonly: propReadonly,
    onChange: propOnChange,
  } = props;
  const auth = useContext(AuthContext);
  const formContext = registry?.formContext;
  const { sideChannel } = formContext;

  const { resource = 'Options', id: idExpr, label: labelExpr } = lookup || {};

  const readonly = uiReadonly || propReadonly;
  const testIdProps = useTestId($id);

  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const dataPath = useMemo(() => getPathFromId($id), [$id]);

  const { options: lookupOptions, loadingOptions, errorLoadingOptions } = useOptionsLookup({
    lookup,
    rootFormData,
    path: dataPath,
    auth,
    formContext,
  });

  function toTypedValue(value) {
    if (itemsType === 'number') {
      const n = Number(value);
      return Number.isNaN(n) ? value : n;
    }

    return value;
  }

  const options = useMemo(
    () =>
      (lookupOptions || []).map(item => {
        const value = idExpr ? item?.[idExpr] : item?.id;
        const label = labelExpr ? item?.[labelExpr] : item?.label;

        return { value: toTypedValue(value), label, __raw: item };
      }),
    [lookupOptions, idExpr, labelExpr, itemsType]
  );

  const value = useMemo(() => {
    if (!Array.isArray(formData)) return [];
    const set = new Set(formData.map(toTypedValue));

    return (options || []).filter(opt => set.has(opt.value));
  }, [formData, options, itemsType]);

  function onChange(selected) {
    const values = (selected || []).map(opt => opt?.value).filter(v => v !== undefined);
    propOnChange(values, fieldPath);
  }

  return (
    <div className="form-group field field-array" {...testIdProps}>
      {title && title.trim() ? (
        <label className="control-label">
          {title}
          {required ? <span className="required">*</span> : null}
        </label>
      ) : null}
      {loadingOptions ? (
        <div>
          <Loader dots inline>
            <Trans>Loading</Trans> {resource}
          </Loader>
        </div>
      ) : errorLoadingOptions ? (
        <Notification color="danger">
          <div>{errorLoadingOptions.message}</div>
        </Notification>
      ) : (
        <Select
          inputId={$id}
          className="autocomplete-container"
          classNamePrefix="autocomplete"
          menuPortalTarget={document.body}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          loadingMessage={() => <Trans>Loading...</Trans>}
          isMulti
          isDisabled={readonly || disabled}
          options={options || []}
          value={value}
          onChange={onChange}
          closeMenuOnSelect={false}
        />
      )}
    </div>
  );
}

export default LookupMultiSelectDropdownField;
